import { useEffect, useRef, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  CreateSchedulerTripInput,
  useCreateSchedulerTripMutation,
  useDeleteSchedulerTripMutation,
  useGetSchedulerTripByIdQuery,
  useSchedulerReferenceUserOptions,
  useUpdateSchedulerTripMutation,
} from '@/app/services/serviceScheduler';
import {
  addStop,
  closeModal,
  openForEdit,
  patchDraft,
  StopFormValues,
  TripFormValues,
} from '@/app/slices/createTripModalSlice';
import { addToast } from '@/app/slices/toast';
import type { RootState } from '@/app/store';
import {
  Dialog as BaseDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Button,
  Dialog,
  Divider,
  Dropdown,
  InputText,
  ToggleButton,
} from '../compat-controls';
import StopCard from '../trip-stop-card';

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isGuid = (value?: string) => !!value && GUID_REGEX.test(value);

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {children} <span className="text-red-500">*</span>
    </label>
  );
}

function OptionalLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {children}
    </label>
  );
}

export function CreateTripModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open, mode, draft } = useSelector(
    (s: RootState) => s.createTripModal,
  );

  const FREQUENCIES = [
    { label: t('serviceScheduler.annual'), value: 'annual' },
    { label: t('serviceScheduler.monthly'), value: 'monthly' },
    { label: t('serviceScheduler.biMonthly'), value: 'bi-monthly' },
    { label: t('serviceScheduler.weekly'), value: 'weekly' },
  ];
  const { options: pseOptions } = useSchedulerReferenceUserOptions({
    role: 'pse',
    activeOnly: true,
    startRow: 1,
    endRow: 200,
  });
  const staffOptions = pseOptions;
  const [createTrip, { isLoading: isCreating }] =
    useCreateSchedulerTripMutation();
  const [updateTrip, { isLoading: isUpdating }] =
    useUpdateSchedulerTripMutation();
  const [deleteTrip, { isLoading: isDeleting }] =
    useDeleteSchedulerTripMutation();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const tripIdForQuery =
    mode === 'edit' && open && isGuid(draft?.id) ? draft.id : skipToken;
  const { data: tripDetail, isFetching: isFetchingDetail } =
    useGetSchedulerTripByIdQuery(tripIdForQuery);
  const hydratedTripIdRef = useRef<string | null>(null);
  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isDeleting || isFetchingDetail;

  useEffect(() => {
    if (!open) {
      hydratedTripIdRef.current = null;
      return;
    }
    if (mode !== 'edit' || !draft?.id || !tripDetail) return;
    if (hydratedTripIdRef.current === tripDetail.tripId) return;

    const mappedDraft: TripFormValues = {
      id: tripDetail.tripId,
      recurring: tripDetail.recurring,
      frequency:
        (tripDetail.frequency as
          | 'annual'
          | 'monthly'
          | 'bi-monthly'
          | 'weekly') || 'monthly',
      shipmentNumber: tripDetail.shipmentNumber ?? '',
      psEmbryologist: tripDetail.psEmbryologistId ?? null,
      psIntern: tripDetail.psInternId ?? null,
      stops: (tripDetail.stops ?? []).map((stop, idx) => ({
        id: stop.stopId || `${tripDetail.tripId}-stop-${idx}`,
        region: stop.region ?? null,
        site: stop.siteCode ?? null,
        startDate: stop.startUtc ?? null,
        endDate: stop.endUtc ?? null,
        psVet: stop.psVetId ?? null,
        accountNumber: stop.accountNumber ?? '',
        description: stop.description ?? '',
        comments: stop.comments ?? '',
        enteredBy: stop.enteredBy ?? '',
        status: stop.status ?? 'tentative',
        opu: stop.opu ?? null,
        ftTx: stop.ftTx ?? null,
        fzTx: stop.fzTx ?? null,
        dfrs: stop.dfrs ?? null,
        pregs: stop.pregs ?? null,
        flush: stop.flush ?? null,
      })),
    };

    dispatch(openForEdit(mappedDraft));
    hydratedTripIdRef.current = tripDetail.tripId;
  }, [dispatch, draft?.id, mode, open, tripDetail]);

  if (!draft) return null;

  const orderedStops: StopFormValues[] = [...draft.stops]
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      if (!a.s.startDate && !b.s.startDate) return a.i - b.i;
      if (!a.s.startDate) return 1;
      if (!b.s.startDate) return -1;

      const diff =
        new Date(a.s.startDate).getTime() - new Date(b.s.startDate).getTime();

      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ s }) => s);

  const hide = () => dispatch(closeModal());

  const mapTripInput = (): CreateSchedulerTripInput => ({
    recurring: draft.recurring,
    frequency: draft.frequency,
    shipmentNumber: draft.shipmentNumber,
    psEmbryologistId: draft.psEmbryologist,
    psInternId: draft.psIntern,
    stops: draft.stops.map((stop) => ({
      ...(isGuid(stop.id) ? { stopId: stop.id } : {}),
      region: stop.region,
      siteCode: stop.site,
      startUtc: stop.startDate,
      endUtc: stop.endDate,
      psVetId: stop.psVet,
      accountNumber: stop.accountNumber,
      description: stop.description,
      comments: stop.comments,
      enteredBy: stop.enteredBy,
      status: stop.status,
      opu: stop.opu,
      ftTx: stop.ftTx,
      fzTx: stop.fzTx,
      dfrs: stop.dfrs,
      pregs: stop.pregs,
      flush: stop.flush,
    })),
  });

  const hasShipmentNumber = !!draft.shipmentNumber?.trim();
  const hasStops = draft.stops.length > 0;
  const hasValidStops = draft.stops.every(
    (stop) => !!stop.site && !!stop.startDate && !!stop.endDate,
  );
  const hasInvalidStopDateRange = draft.stops.some((stop) => {
    if (!stop.startDate || !stop.endDate) return false;
    return new Date(stop.startDate) > new Date(stop.endDate);
  });
  const isFormValid =
    hasShipmentNumber && hasStops && hasValidStops && !hasInvalidStopDateRange;

  const saveTrip = async () => {
    if (!isFormValid) {
      const missingFields: string[] = [];
      if (!hasShipmentNumber) missingFields.push(t('shipping.shipmentNumber'));
      if (!hasStops) missingFields.push(t('serviceScheduler.stop'));
      if (hasStops && !hasValidStops) {
        missingFields.push(
          `${t('site.site')}/${t('common.startDate')}/${t('common.endDate')}`,
        );
      }
      const detail = hasInvalidStopDateRange
        ? `${t('common.startDate')} > ${t('common.endDate')}`
        : `${t('common.missingFields')}: ${missingFields.join(', ')}`;
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.validationError'),
          detail,
          sticky: true,
        }),
      );
      return;
    }

    try {
      const input = mapTripInput();
      if (mode === 'edit' && draft.id) {
        await updateTrip({ tripId: draft.id, input }).unwrap();
      } else {
        await createTrip(input).unwrap();
      }

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.save'),
          detail:
            mode === 'edit'
              ? t('serviceScheduler.editTrip')
              : t('serviceScheduler.createTrip'),
        }),
      );
      hide();
    } catch (error: unknown) {
      const errObj = error as { data?: unknown } | undefined;
      const detail =
        typeof errObj?.data === 'string' ? errObj.data : t('common.error');
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail,
          sticky: true,
        }),
      );
    }
  };

  const handleDelete = async () => {
    if (!draft?.id || mode !== 'edit') return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!draft?.id || mode !== 'edit') return;
    try {
      await deleteTrip({ tripId: draft.id }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.delete'),
          detail: t('serviceScheduler.editTrip'),
        }),
      );
      hide();
    } catch (error: unknown) {
      const errObj = error as { data?: unknown } | undefined;
      const detail =
        typeof errObj?.data === 'string' ? errObj.data : t('common.error');
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail,
          sticky: true,
        }),
      );
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <>
      <Dialog
        visible={open}
        header={
          <span className="text-lg font-semibold">
            {mode === 'create'
              ? t('serviceScheduler.createTrip')
              : t('serviceScheduler.editTrip')}
          </span>
        }
        footer={
          <div className="flex w-full justify-between">
            {mode === 'edit' && (
              <Button
                label={t('common.delete')}
                severity="danger"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={isBusy}
              />
            )}
            <div className="ml-auto flex gap-2">
              <Button
                label={t('common.cancel')}
                severity="secondary"
                onClick={hide}
                disabled={isBusy}
              />
              <Button
                label={t('common.save')}
                icon="pi pi-check"
                onClick={saveTrip}
                loading={isSaving}
                disabled={isBusy || !isFormValid}
              />
            </div>
          </div>
        }
        onHide={hide}
        style={{ width: '60vw', maxWidth: 720 }}
        breakpoints={{ '960px': '95vw' }}
        dismissableMask
        blockScroll
      >
        <div className="space-y-4">
          <div>
            <RequiredLabel>{t('shipping.shipmentNumber')}</RequiredLabel>
            <InputText
              placeholder={t('shipping.shipmentNumber')}
              value={draft.shipmentNumber}
              onChange={(e) =>
                dispatch(patchDraft({ shipmentNumber: e.target.value }))
              }
              className="w-full"
            />
          </div>

          <div className="flex items-center justify-between">
            <OptionalLabel>{t('serviceScheduler.recurring')}</OptionalLabel>
            <ToggleButton
              checked={draft.recurring}
              onChange={(e) => dispatch(patchDraft({ recurring: e.value }))}
            />
          </div>

          {draft.recurring && (
            <div>
              <RequiredLabel>{t('serviceScheduler.frequency')}</RequiredLabel>
              <Dropdown
                value={draft.frequency}
                options={FREQUENCIES}
                placeholder={t('serviceScheduler.frequency')}
                onChange={(e) =>
                  dispatch(
                    patchDraft({
                      frequency: e.value as
                        | 'annual'
                        | 'monthly'
                        | 'bi-monthly'
                        | 'weekly',
                    }),
                  )
                }
                className="w-full"
              />
            </div>
          )}

          <Divider />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <OptionalLabel>
                {t('serviceScheduler.psEmbryologist')}
              </OptionalLabel>
              <div className="flex gap-2">
                <Dropdown
                  value={draft.psEmbryologist}
                  options={staffOptions}
                  placeholder={t('serviceScheduler.psEmbryologist')}
                  onChange={(e) =>
                    dispatch(patchDraft({ psEmbryologist: e.value }))
                  }
                  className="w-full"
                />
                <Button
                  type="button"
                  label={t('common.clear')}
                  severity="secondary"
                  onClick={() => dispatch(patchDraft({ psEmbryologist: null }))}
                  disabled={!draft.psEmbryologist || isBusy}
                />
              </div>
            </div>
            <div>
              <OptionalLabel>{t('serviceScheduler.psIntern')}</OptionalLabel>
              <div className="flex gap-2">
                <Dropdown
                  value={draft.psIntern}
                  options={staffOptions}
                  placeholder={t('serviceScheduler.psIntern')}
                  onChange={(e) => dispatch(patchDraft({ psIntern: e.value }))}
                  className="w-full"
                />
                <Button
                  type="button"
                  label={t('common.clear')}
                  severity="secondary"
                  onClick={() => dispatch(patchDraft({ psIntern: null }))}
                  disabled={!draft.psIntern || isBusy}
                />
              </div>
            </div>
          </div>

          <Divider />

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {t('serviceScheduler.stop')}s{' '}
                <span className="text-red-500">*</span>
              </span>
              <span className="text-xs text-gray-400">
                {t('serviceScheduler.stop')}: {t('site.site')},{' '}
                {t('common.startDate')}, {t('common.endDate')}{' '}
                {t('common.required').toLowerCase()}
              </span>
            </div>

            {orderedStops.map((stop, idx) => (
              <StopCard displayIndex={idx} key={stop.id} stopId={stop.id} />
            ))}

            <Button
              label={t('serviceScheduler.addStop')}
              icon="pi pi-plus"
              className="w-full"
              onClick={() => dispatch(addStop())}
            />
          </div>
        </div>
      </Dialog>

      <BaseDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('common.delete')}</DialogTitle>
            <DialogDescription>{t('common.areYouSure')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              label={t('common.cancel')}
              severity="secondary"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={isDeleting}
            />
            <Button
              label={t('common.delete')}
              severity="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            />
          </DialogFooter>
        </DialogContent>
      </BaseDialog>
    </>
  );
}
